/**
 * v3.18-1296 — Embeddable Communications tab for Client / Company
 * detail pages.
 *
 * Renders the client's (or company's) authoritative directory
 * without leaving the page. Operators can add/edit contacts, call,
 * SMS, and email inline. Every entry displays live local time and
 * quiet-hour indicator.
 *
 * Props:
 *   clientId?  string   — scope to a single client's contacts
 *   companyId  string   — company scope (required for creates)
 *   canWrite   boolean  — enable create/edit/delete
 *   compact    boolean  — collapses row density for panels
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Star, Phone, MessageSquare, Mail, Pencil, Trash2, Users,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ContactEditorDialog from './ContactEditorDialog';
import ClientLocalTime from './ClientLocalTime';
import {
  listContacts, listGroups, deleteContact, updateContact,
  primaryPhone, primaryEmail, humanizeAgo,
} from '../lib/communication_contacts';
import { dispatchDial, dispatchCompose } from '../lib/comm_bus';
import { canUsePhoneSystem } from '../lib/phone_access';
import api from '../lib/api';

const ClientCommunicationsTab = ({
  clientId, companyId, canWrite = true, compact = true,
}) => {
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        setMe(data);
      } catch (_e) { /* noop */ }
    })();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        listContacts({
          company_id: companyId || undefined,
          client_id: clientId,
          with_last_contacted: true,
        }),
        companyId ? listGroups({ company_id: companyId }) : Promise.resolve({ groups: [] }),
      ]);
      setContacts(c.contacts || []);
      setGroups(g.groups || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load contacts');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [clientId, companyId]);

  const phoneEnabled = canUsePhoneSystem(me);

  const toggleFavorite = async (c) => {
    try {
      await updateContact(c.id, { is_favorite: !c.is_favorite });
      setContacts((rows) => rows.map((x) => (x.id === c.id ? { ...x, is_favorite: !c.is_favorite } : x)));
    } catch (_e) { toast.error('Failed'); }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Delete contact "${c.full_name}"?`)) return;
    try { await deleteContact(c.id); refresh(); toast.success('Deleted'); }
    catch (_e) { toast.error('Delete failed'); }
  };

  const call = (contact, phone) => {
    if (!phone?.number) return;
    if (phone.do_not_call) { toast.error('Do Not Call flag set'); return; }
    if (!phoneEnabled) { toast.info('Phone system access not enabled for your company'); return; }
    dispatchDial({ number: phone.number, contact_id: contact.id, client_id: contact.client_id });
  };
  const email = (contact, addr) => {
    if (!addr?.address) return;
    dispatchCompose({ to: addr.address, contact_id: contact.id, client_id: contact.client_id });
  };

  return (
    <div data-testid="client-comms-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-800">Communication Contacts</h3>
          <Badge variant="outline" className="text-[10px]">{contacts.length}</Badge>
        </div>
        {canWrite && companyId && (
          <Button
            size="sm" variant="outline"
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            data-testid="client-comms-add-contact"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add contact
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 py-4">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="text-xs text-slate-400 py-4" data-testid="client-comms-empty">
          No contacts yet. {canWrite && 'Add one so operators never dial cold.'}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          {contacts.map((c) => {
            const p = primaryPhone(c);
            const e = primaryEmail(c);
            return (
              <div
                key={c.id}
                className={`flex items-center gap-2 border-b border-slate-100 hover:bg-slate-50 transition
                  ${compact ? 'px-2 py-1.5' : 'px-3 py-2.5'}`}
                data-testid={`client-comms-row-${c.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleFavorite(c)}
                  className={`h-6 w-6 rounded flex items-center justify-center
                    ${c.is_favorite ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}
                >
                  <Star className="h-3.5 w-3.5" fill={c.is_favorite ? 'currentColor' : 'none'} />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 truncate">{c.full_name}</span>
                    {c.is_primary && (
                      <span className="text-[10px] px-1.5 rounded-full bg-emerald-100 text-emerald-700">
                        Primary
                      </span>
                    )}
                    {c.title && <span className="text-xs text-slate-500 truncate">· {c.title}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-slate-600">
                    {p && (
                      <span className={p.do_not_call ? 'line-through text-red-500' : ''}>
                        {p.label}: {p.number}
                      </span>
                    )}
                    {e && <span>{e.label}: {e.address}</span>}
                    <ClientLocalTime
                      timezone={c.timezone || undefined}
                      clientId={c.timezone ? undefined : c.client_id}
                      variant="chip"
                    />
                    {c.last_contacted_at && (
                      <span className="text-slate-400">· {humanizeAgo(c.last_contacted_at)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => call(c, p)}
                    disabled={!p || p?.do_not_call}
                    className="h-7 w-7 rounded flex items-center justify-center text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-30"
                    title="Call"
                    data-testid={`client-comms-call-${c.id}`}
                  >
                    <Phone className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => call(c, p)}
                    disabled={!p || p?.do_not_call}
                    className="h-7 w-7 rounded flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30"
                    title="SMS"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => email(c, e)}
                    disabled={!e}
                    className="h-7 w-7 rounded flex items-center justify-center text-slate-500 hover:bg-purple-50 hover:text-purple-600 disabled:opacity-30"
                    title="Email"
                    data-testid={`client-comms-email-${c.id}`}
                  >
                    <Mail className="h-3.5 w-3.5" />
                  </button>
                  {canWrite && (
                    <>
                      <button
                        type="button"
                        onClick={() => { setEditing(c); setEditorOpen(true); }}
                        className="h-7 w-7 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100"
                        title="Edit"
                        data-testid={`client-comms-edit-${c.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeContact(c)}
                        className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ContactEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        contact={editing}
        companyId={companyId}
        clientId={clientId}
        groups={groups}
        onSaved={refresh}
      />
    </div>
  );
};

export default ClientCommunicationsTab;
